import { Box } from '@mui/material';
import { ColDef } from 'ag-grid-community';
import { AgGridReact, type AgGridReactProps } from 'ag-grid-react';
import { useRef } from 'react';

import '@/shared/components/ui/data-table/ag-grid-modules';

import {
  CONTEXT_MENU_CELL_ID,
  FIXED_COL_IDS,
  MIN_COLUMN_WIDTH,
  SELECT_CELL_ID,
  WIDE_COL_IDS,
  WIDE_COLUMN_FLEX,
} from '@/shared/components/ui/data-table/components/ag-grid-table-core/ag-grid-table-core.constants';
import { TAgGridTableCoreProps } from '@/shared/components/ui/data-table/components/ag-grid-table-core/ag-grid-table-core.types';
import { useDataTable } from '@/shared/components/ui/data-table/context/data-table-context';
import { DefaultCellRenderer } from '@/shared/components/ui/data-table/data-table.renderer';
import { useAgGridMuiTheme } from '@/shared/components/ui/data-table/hooks/use-ag-grid-theme/use-ag-grid-theme';
import { useFilterScope } from '@/shared/features/filter-preferences/context/filter-scope-context';

const IGNORE_CLICK_CELLS = [SELECT_CELL_ID, CONTEXT_MENU_CELL_ID];

// Most definitions identify the pinned columns by field rather than colId.
const isFixedColumn = (col: ColDef) =>
  FIXED_COL_IDS.includes(col.colId ?? col.field ?? '');

const isWideColumn = (col: ColDef) =>
  WIDE_COL_IDS.includes(col.colId ?? col.field ?? '');

// Never raise a floor a column already set below it.
const resizeFloor = (col: ColDef) =>
  Math.min(col.minWidth ?? MIN_COLUMN_WIDTH, MIN_COLUMN_WIDTH);

const HEADER_HEIGHT = 48;
const ROW_HEIGHT = 48;

export const AgGridTableCore = <T,>({
  noRowsOverlayComponent,
  loadingOverlayComponent,
  onItemClick,
  disableSelection,
}: TAgGridTableCoreProps<T>) => {
  const {
    data,
    colDefs,
    getItemId,
    setSelectedItems,
    setGridApi,
    disableItemCheckbox,
    disableItemClick,
  } = useDataTable<T>();
  const { items = [], isLoading, isFetching } = data;
  const { isReady } = useFilterScope();
  const agGridTheme = useAgGridMuiTheme();
  const gridRef = useRef<AgGridReact>(null);

  // A gated (skipped) query reports isLoading false, so treat "scope not ready"
  // as loading — otherwise the empty-state flashes before the first rows.
  const _isLoading = !isReady || isLoading || isFetching;

  const handleSelectionChanged = (selectedItems: T[]) =>
    setSelectedItems(selectedItems);

  const getRowSelection = (): AgGridReactProps<T>['rowSelection'] => {
    if (disableSelection) {
      return undefined;
    }

    return {
      mode: 'multiRow',
      isRowSelectable: ({ data }) =>
        data && disableItemCheckbox ? !disableItemCheckbox(data) : true,
    };
  };

  // Process column definitions to ensure proper flex setup
  const processedColDefs = colDefs?.map((col) => {
    // Fixed width columns (checkbox, context menu)
    if (col.colId === SELECT_CELL_ID) {
      return {
        ...col,
        width: 50,
        maxWidth: 50,
        minWidth: 50,
        resizable: false,
        suppressSizeToFit: true,
      };
    }

    if (col.colId === CONTEXT_MENU_CELL_ID) {
      return {
        ...col,
        width: 150,
        maxWidth: 150,
        minWidth: 150,
        resizable: false,
        suppressSizeToFit: true,
      };
    }

    if (isFixedColumn(col)) {
      return col;
    }

    // Its own width would pin it while every neighbour flexes past it.
    if (isWideColumn(col)) {
      const { width: _fixed, ...rest } = col;

      return { ...rest, flex: WIDE_COLUMN_FLEX, minWidth: resizeFloor(col) };
    }

    if (!col.flex && !col.width) {
      return {
        ...col,
        flex: 1,
        minWidth: resizeFloor(col),
      };
    }

    return { ...col, minWidth: resizeFloor(col) };
  });

  // The trailing column is never resizable.
  const finalColDefs = processedColDefs?.map((col, index, arr) =>
    index === arr.length - 1 ? { ...col, resizable: false } : col,
  );

  return (
    <Box height="100%" position="relative">
      <AgGridReact
        ref={gridRef}
        theme={agGridTheme}
        rowData={items}
        columnDefs={finalColDefs}
        onGridReady={(params) => {
          setGridApi?.(params.api);
        }}
        rowModelType="clientSide"
        suppressPaginationPanel={false}
        defaultColDef={{
          sortable: true,
          filter: false,
          resizable: true,
          cellRenderer: DefaultCellRenderer,
          flex: 1, // Default flex for columns
          minWidth: MIN_COLUMN_WIDTH,
          cellStyle: (params) => {
            const baseStyle = {
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              height: '100%',
            };

            if (params.column.getColId() === CONTEXT_MENU_CELL_ID) {
              return {
                ...baseStyle,
                alignItems: 'flex-end',
                textAlign: 'right',
              };
            }

            return {
              ...baseStyle,
              textAlign: 'left',
            };
          },
        }}
        animateRows={true}
        loading={_isLoading}
        getRowId={(props) => {
          return getItemId(props.data);
        }}
        rowSelection={getRowSelection()}
        headerHeight={HEADER_HEIGHT}
        rowHeight={ROW_HEIGHT}
        onSelectionChanged={(props) => {
          const selectedNodes = props.selectedNodes;
          if (!selectedNodes) {
            return;
          }

          handleSelectionChanged(selectedNodes.map((node) => node.data));
        }}
        onCellClicked={(props) => {
          if (IGNORE_CLICK_CELLS.includes(props.column.getColId())) {
            return;
          }

          if (props.data && disableItemClick?.(props.data)) {
            return;
          }

          if (onItemClick) {
            onItemClick(props.data);
            return;
          }
        }}
        suppressCellFocus={true}
        enableCellTextSelection={true}
        suppressRowHoverHighlight={false}
        noRowsOverlayComponent={noRowsOverlayComponent}
        loadingOverlayComponent={loadingOverlayComponent}
        // This ensures the grid always fills the container
        domLayout="normal"
        // Allow column resizing but maintain total width
        maintainColumnOrder={true}
        // When columns are resized, other flex columns adjust automatically
        alwaysShowHorizontalScroll={false}
      />
    </Box>
  );
};
